import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Music, Send, Loader2, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SongRequest() {
  const { user } = useAuth();
  const [artist, setArtist] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, sending, success, error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!artist.trim() || !title.trim()) return;
    
    setStatus('sending');
    setErrorMsg('');
    
    try {
      const response = await fetch(`${API_URL}/api/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          artist: artist.trim(),
          title: title.trim(),
          message: message.trim() || null
        })
      });

      if (response.ok) {
        setStatus('success');
        setArtist('');
        setTitle('');
        setMessage('');
        setShowMessage(false);
        setTimeout(() => setStatus('idle'), 4000);
      } else {
        const data = await response.json();
        setErrorMsg(data.detail || 'Failed to submit request');
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
      }
    } catch (error) {
      setErrorMsg('Network error. Please try again.');
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div 
      className="glass rounded-2xl p-4"
      data-testid="song-request-panel"
    >
      <div className="flex items-center gap-2 mb-4">
        <Music className="w-5 h-5" style={{ color: 'var(--secondary)' }} />
        <h3 
          className="text-lg font-bold"
          style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
        >
          Request a Song
        </h3>
      </div>

      {status === 'success' ? (
        <div 
          className="flex items-center gap-2 p-4 rounded-xl"
          style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}
        >
          <CheckCircle className="w-5 h-5" style={{ color: '#22C55E' }} />
          <div>
            <p className="text-sm font-medium" style={{ color: '#22C55E' }}>
              Request submitted!
            </p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              The DJ will see your request
            </p>
          </div>
        </div>
      ) : status === 'error' ? (
        <div 
          className="flex items-center gap-2 p-4 rounded-xl"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}
        >
          <AlertCircle className="w-5 h-5" style={{ color: '#EF4444' }} />
          <p className="text-sm" style={{ color: '#EF4444' }}>
            {errorMsg}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            placeholder="Artist name"
            value={artist}
            onChange={(e) => setArtist(e.target.value)}
            className="w-full p-3 rounded-xl text-sm outline-none transition-all focus:ring-2"
            style={{ 
              backgroundColor: 'var(--surface-highlight)',
              color: 'var(--text-main)'
            }}
            data-testid="request-artist"
          />
          
          <input
            type="text"
            placeholder="Song title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-3 rounded-xl text-sm outline-none transition-all focus:ring-2"
            style={{ 
              backgroundColor: 'var(--surface-highlight)',
              color: 'var(--text-main)'
            }}
            data-testid="request-title"
          />

          {/* Optional message toggle */}
          {!showMessage ? (
            <button
              type="button"
              onClick={() => setShowMessage(true)}
              className="flex items-center gap-1 text-xs transition-colors"
              style={{ color: 'var(--text-muted)' }}
            >
              <MessageSquare className="w-3 h-3" />
              Add a message for the DJ
            </button>
          ) : (
            <textarea
              placeholder="Message for the DJ (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              className="w-full p-3 rounded-xl text-sm outline-none transition-all focus:ring-2 resize-none"
              style={{ 
                backgroundColor: 'var(--surface-highlight)',
                color: 'var(--text-main)'
              }}
              data-testid="request-message"
            />
          )}

          {/* Show logged in status */}
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {user ? `Requesting as ${user.name}` : 'Requesting as Anonymous'}
          </p>
          
          <button
            type="submit"
            disabled={status === 'sending' || !artist.trim() || !title.trim()}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-xl font-medium transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
            style={{ 
              background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
              color: 'white'
            }}
            data-testid="request-submit"
          >
            {status === 'sending' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            <span>{status === 'sending' ? 'Sending...' : 'Send Request'}</span>
          </button>
        </form>
      )}
    </div>
  );
}
